import { useCallback, useSyncExternalStore } from 'react';

/** Which `Storage` a preference is persisted to. */
export type UiPreferenceStorageArea = 'localStorage' | 'sessionStorage';

// Both storage areas persist under this same key. A write to either area dispatches
// a `storage` event that wakes subscribers reading from both; a subscriber reading
// from the untouched area re-reads its own storage, gets back the same value, and
// `useSyncExternalStore` bails out on the unchanged snapshot without re-rendering.
const STORAGE_KEY = 'zui-preferences';

const resolveStorage = (area: UiPreferenceStorageArea): Storage => window[area];

const subscribe = (callback: () => void) => {
  window.addEventListener('storage', callback);
  return () => window.removeEventListener('storage', callback);
};

const readAll = (area: UiPreferenceStorageArea): Record<string, unknown> => {
  try {
    const raw = resolveStorage(area).getItem(STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const parsed: unknown = JSON.parse(raw);
    // Reject anything that isn't a plain object — e.g. the string `"null"` parses
    // to JS `null`, `"42"` parses to a number, `"[1,2,3]"` parses to an array.
    // Returning any of these would let callers index into a non-record and throw.
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }
    return parsed as Record<string, unknown>;
  } catch {
    return {};
  }
};

const writeKey = (area: UiPreferenceStorageArea, key: string, value: unknown): void => {
  const next = { ...readAll(area), [key]: value };
  try {
    const serialized = JSON.stringify(next);
    resolveStorage(area).setItem(STORAGE_KEY, serialized);
    // localStorage only fires `storage` events across tabs, and sessionStorage never
    // does — dispatch synthetically so same-tab `useSyncExternalStore` subscribers
    // observe the change too.
    window.dispatchEvent(new StorageEvent('storage', { key: STORAGE_KEY, newValue: serialized }));
  } catch {
    /* pass — fall back to not persisting rather than crashing the caller */
  }
};

/**
 * Reads a single boolean key from the `zui-preferences` entry in the given storage
 * area (`localStorage` by default) and re-renders only when that key's value changes.
 */
export const useUiFlag = (
  key: string,
  defaultValue: boolean,
  area: UiPreferenceStorageArea = 'localStorage',
): [boolean, (next: boolean) => void] => {
  const getSnapshot = useCallback((): boolean => {
    const value = readAll(area)[key];
    return typeof value === 'boolean' ? value : defaultValue;
  }, [key, defaultValue, area]);

  const value = useSyncExternalStore(subscribe, getSnapshot, () => defaultValue);

  const setValue = useCallback((next: boolean) => writeKey(area, key, next), [key, area]);

  return [value, setValue];
};

/**
 * Reads a single string key from the `zui-preferences` entry in the given storage
 * area (`localStorage` by default) and re-renders only when that key's value changes.
 */
export const useUiPreference = (
  key: string,
  defaultValue: string,
  area: UiPreferenceStorageArea = 'localStorage',
): [string, (next: string) => void] => {
  const getSnapshot = useCallback((): string => {
    const value = readAll(area)[key];
    return typeof value === 'string' ? value : defaultValue;
  }, [key, defaultValue, area]);

  const value = useSyncExternalStore(subscribe, getSnapshot, () => defaultValue);

  const setValue = useCallback((next: string) => writeKey(area, key, next), [key, area]);

  return [value, setValue];
};
